"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { CheckIcon } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Helper function to safely render change values
const renderChangeValue = (value) => {
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc list-inside ml-4 text-gray-300">
        {value.map((item, index) => (
          <li key={item._id?.$oid || index}>
            {item.title || item.itemDescription || "Unnamed Item"}: $
            {item.amount || 0}
          </li>
        ))}
      </ul>
    );
  } else if (value && typeof value === "object") {
    return JSON.stringify(value);
  } else {
    return String(value || "N/A");
  }
};

export default function ProjectTimeline() {
  const { id } = useParams();
  const [timeline, setTimeline] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/project-timeline/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const normalizedTimeline = response.data.data.map((event) => ({
          id: event._id?.$oid || event._id,
          changes: event.changes || {},
          projectId: event.projectId?.$oid || event.projectId,
          entityType: event.entityType,
          entityId: event.entityId?.$oid || event.entityId,
          action: event.action,
          description: event.description,
          changedBy: event.changedBy?.$oid || event.changedBy || "Unknown User",
          timestamp: event.timestamp?.$date || event.timestamp,
        }));
        setTimeline(normalizedTimeline);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load timeline");
      } finally {
        setIsLoading(false);
      }
    };
    fetchTimeline();
  }, [id]);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "N/A";

  const getEventStatus = (event, index) => {
    const now = new Date();
    const eventDate = new Date(event.timestamp);
    if (eventDate <= now) {
      return index === 0 ? "current" : "complete";
    }
    return "upcoming";
  };

  if (isLoading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  if (error)
    return <div className="text-red-400 text-center py-4">{error}</div>;

  return (
    <div className="max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-700">
      <nav aria-label="Timeline">
        <ol role="list" className="overflow-hidden">
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-300">
              No timeline events available.
            </p>
          ) : (
            timeline.map((event, eventIdx) => {
              const status = getEventStatus(event, eventIdx);
              return (
                <li
                  key={event.id}
                  className={classNames(
                    eventIdx !== timeline.length - 1 ? "pb-10" : "",
                    "relative"
                  )}
                >
                  {status === "complete" ? (
                    <>
                      {eventIdx !== timeline.length - 1 ? (
                        <div
                          aria-hidden="true"
                          className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-orange-400"
                        />
                      ) : null}
                      <div className="group relative flex items-start">
                        <span className="flex h-9 items-center">
                          <span className="relative z-10 flex size-8 items-center justify-center rounded-full bg-orange-400 group-hover:bg-orange-500">
                            <CheckIcon
                              aria-hidden="true"
                              className="size-5 text-gray-700"
                            />
                          </span>
                        </span>
                        <span className="ml-4 flex min-w-0 flex-col">
                          <span className="text-sm font-medium text-gray-200">
                            {event.action.charAt(0).toUpperCase() +
                              event.action.slice(1)}{" "}
                            {event.entityType}
                          </span>
                          <span className="text-sm text-gray-300">
                            {event.description}
                            <br />
                            By {event.changedBy} on{" "}
                            {formatDate(event.timestamp)}
                          </span>
                          {Object.keys(event.changes).length > 0 && (
                            <details className="mt-1">
                              <summary className="text-sm text-orange-400 cursor-pointer hover:text-orange-300">
                                View Changes
                              </summary>
                              <ul className="list-disc list-inside text-sm text-gray-300">
                                {Object.entries(event.changes).map(
                                  ([key, value]) => (
                                    <li key={key}>
                                      <strong>{key}:</strong>{" "}
                                      {renderChangeValue(value.old)} →{" "}
                                      {renderChangeValue(value.new)}
                                    </li>
                                  )
                                )}
                              </ul>
                            </details>
                          )}
                        </span>
                      </div>
                    </>
                  ) : status === "current" ? (
                    <>
                      {eventIdx !== timeline.length - 1 ? (
                        <div
                          aria-hidden="true"
                          className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-600"
                        />
                      ) : null}
                      <div
                        aria-current="step"
                        className="group relative flex items-start"
                      >
                        <span
                          aria-hidden="true"
                          className="flex h-9 items-center"
                        >
                          <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-orange-400 bg-gray-700">
                            <span className="size-2.5 rounded-full bg-orange-400" />
                          </span>
                        </span>
                        <span className="ml-4 flex min-w-0 flex-col">
                          <span className="text-sm font-medium text-orange-400">
                            {event.action.charAt(0).toUpperCase() +
                              event.action.slice(1)}{" "}
                            {event.entityType}
                          </span>
                          <span className="text-sm text-gray-300">
                            {event.description}
                            <br />
                            By {event.changedBy} on{" "}
                            {formatDate(event.timestamp)}
                          </span>
                          {Object.keys(event.changes).length > 0 && (
                            <details className="mt-1">
                              <summary className="text-sm text-orange-400 cursor-pointer hover:text-orange-300">
                                View Changes
                              </summary>
                              <ul className="list-disc list-inside text-sm text-gray-300">
                                {Object.entries(event.changes).map(
                                  ([key, value]) => (
                                    <li key={key}>
                                      <strong>{key}:</strong>{" "}
                                      {renderChangeValue(value.old)} →{" "}
                                      {renderChangeValue(value.new)}
                                    </li>
                                  )
                                )}
                              </ul>
                            </details>
                          )}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {eventIdx !== timeline.length - 1 ? (
                        <div
                          aria-hidden="true"
                          className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-600"
                        />
                      ) : null}
                      <div className="group relative flex items-start">
                        <span
                          aria-hidden="true"
                          className="flex h-9 items-center"
                        >
                          <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-600 bg-gray-700 group-hover:border-gray-500">
                            <span className="size-2.5 rounded-full bg-transparent group-hover:bg-gray-600" />
                          </span>
                        </span>
                        <span className="ml-4 flex min-w-0 flex-col">
                          <span className="text-sm font-medium text-gray-400">
                            {event.action.charAt(0).toUpperCase() +
                              event.action.slice(1)}{" "}
                            {event.entityType}
                          </span>
                          <span className="text-sm text-gray-300">
                            {event.description}
                            <br />
                            By {event.changedBy} on{" "}
                            {formatDate(event.timestamp)}
                          </span>
                          {Object.keys(event.changes).length > 0 && (
                            <details className="mt-1">
                              <summary className="text-sm text-orange-400 cursor-pointer hover:text-orange-300">
                                View Changes
                              </summary>
                              <ul className="list-disc list-inside text-sm text-gray-300">
                                {Object.entries(event.changes).map(
                                  ([key, value]) => (
                                    <li key={key}>
                                      <strong>{key}:</strong>{" "}
                                      {renderChangeValue(value.old)} →{" "}
                                      {renderChangeValue(value.new)}
                                    </li>
                                  )
                                )}
                              </ul>
                            </details>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </li>
              );
            })
          )}
        </ol>
      </nav>
    </div>
  );
}
